import { dateLocale, m } from "./i18n";

export { ordinal } from "./i18n/en";

type Child = Node | string | number | false | null | undefined;
type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

/**
 * Builds an element. `on*` attributes become listeners, `true` sets a boolean
 * attribute, and `false`/`undefined` leave it off. Text is always set as text,
 * never HTML, so user-entered names can't inject markup.
 */
export function h(tag: string, attrs: Attrs = {}, ...children: Child[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (typeof value === "function") el.addEventListener(key.slice(2), value);
    else el.setAttribute(key, value === true ? "" : String(value));
  }
  for (const child of children) {
    if (child === false || child === null || child === undefined) continue;
    el.append(child instanceof Node ? child : String(child));
  }
  return el;
}

/** Replaces root's content; null entries are skipped. */
export function mount(root: HTMLElement, ...nodes: (Node | null)[]): void {
  root.replaceChildren(...nodes.filter((n): n is Node => n !== null));
}

export function photoUrl(key: string): string {
  return `/api/photos/${key}`;
}

/**
 * An event date (YYYY-MM-DD) as people say it, in the app's language, inside
 * a <time> that keeps the machine-readable form. The date has no time zone, so
 * it is formatted as UTC to stop it slipping a day.
 */
export function dateEl(iso: string, cls?: string): HTMLElement {
  const date = new Date(`${iso}T00:00:00Z`);
  const text = Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(dateLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  return h("time", { datetime: iso, class: cls }, text);
}

/**
 * The "run your own cookoff" line (SW-59). A full page load on purpose, not an
 * in-app link: the Worker counts ?ref per surface, because Web Analytics drops
 * query strings.
 */
export function madeWith(ref: "results" | "reveal"): HTMLElement {
  return h("p", { class: "made-with" }, h("a", { href: `/?ref=${ref}` }, m().common.runYourOwn));
}

/** The rosette mark. Decorative wherever it appears next to the name. */
export function logo(cls = "logo"): HTMLElement {
  return h("img", { src: "/favicon.svg", alt: "", class: cls, width: 104, height: 104 });
}
